import { useRef, useState } from "react";
import { useMember } from "../../context/memberContext";
import useListFilters from "../../hooks/useListFilters";
import {
  SP,
  deleteMemberInvite,
  useMemberInvites,
} from "../../http/serviceportalApi";
import { useTranslations } from "../../hooks/useTranslations";
import { formatLongDateTime, getRemoteDataStatus } from "../../utils";
import SVGDelete from "../../icons/Delete.svg?react";
import InviteStatus from "../invites/InviteStatus";
import Table from "../ui/Table";
import TableButton, { TableButtonCell } from "../ui/TableButton";

const MemberInvites = () => {
  const { i18n } = useTranslations();
  const { member } = useMember();

  const filters = useListFilters();

  const aborter = useRef(new AbortController());
  const { data, isValidating, error, mutate } = useMemberInvites(
    member?.id,
    filters.state,
    aborter.current.signal,
  );
  const items = data?.items ?? [];
  const hasNextPage = data?.next_page !== undefined;
  const status = getRemoteDataStatus({ isValidating, error });

  if (!member) return null;

  return (
    <Table
      filters={filters}
      head={
        <tr>
          <th align="left">{i18n.invites.columnHeaders.email}</th>
          <th align="left">{i18n.invites.columnHeaders.companies}</th>
          <th align="left">{i18n.invites.columnHeaders.validUntil}</th>
          <th align="left">{i18n.invites.columnHeaders.status}</th>
          <th></th>
        </tr>
      }
      body={items.map((item, index) => (
        <Row
          key={index}
          invite={item}
          onDelete={(invite) =>
            deleteMemberInvite(member.id, invite.id).then(() => {
              mutate({
                ...data!,
                items: [...items.filter((inv) => inv.id !== item.id)],
              });
            })
          }
        />
      ))}
      hasNextPage={hasNextPage}
      remoteDataStatus={status}
    />
  );
};

interface RowProps {
  invite: SP.Invite;
  onDelete: (invite: SP.Invite) => Promise<void>;
}

const Row = ({ invite, onDelete }: RowProps) => {
  const { i18n } = useTranslations();
  const [isDeleting, setDeleting] = useState(false);

  return (
    <tr>
      <td>{invite.email}</td>
      <td>{invite.companyId ? invite.companyName : i18n.allCompanies}</td>
      <td>{!invite.declinedAt && formatLongDateTime(invite.expiresAt)}</td>
      <td>
        <InviteStatus invite={invite} />
      </td>
      <TableButtonCell>
        <TableButton
          glyph={SVGDelete}
          loading={isDeleting}
          onClick={() => {
            setDeleting(true);
            onDelete(invite).finally(() => setDeleting(false));
          }}
        />
      </TableButtonCell>
    </tr>
  );
};

export default MemberInvites;
